import { ChangeDetectorRef, Component, ElementRef, HostListener, inject, ViewChild } from '@angular/core';
import { AsyncPipe, NgFor, NgIf, NgTemplateOutlet } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatRippleModule } from '@angular/material/core';
import { MatExpansionModule } from '@angular/material/expansion';
import { InViewportModule } from 'ng-in-viewport';
import { StorageService } from '@services/storage/storage.service';
import { ActivatedRoute } from '@angular/router';
import { HomepageService } from '@services/homepage/homepage.service';
import { UtilService } from '@services/util/util.service';
import { FundraiserService } from '@services/fundraiser/fundraiser.service';
import { VariablesService } from '@services/variables/variables.service';
import { SeoService } from '@services/seo/seo.service';
import { FbService } from '@services/fb/fb.service';
import { EventsService } from '@services/events/events.service';
import { MatDialog } from '@angular/material/dialog';
import { PaymentUtilService } from '@services/payment-util/payment-util.service';
import { VwoService } from '@services/vwo/vwo.service';
import { AuthService } from '@services/auth/auth.service';
import { MatBottomSheet } from '@angular/material/bottom-sheet';
import { IBasicInfo, IFundraiser } from '@models/fundraiser.model';
import { IUser } from '@models/user.model';
import { Subscription } from 'rxjs';
import { ContactDialogComponent } from '@common-components/contact-dialog/contact-dialog.component';
import { ComponentLoader } from '@core-components/component-loader/component-loader.component';
import { CustomTranslatePipe } from '@pipes/translate/translate.pipe';
import { InrFormatPipe } from '@pipes/inr-format/inr-format.pipe';
import { SafeUrlPipe } from '@pipes/safe-url/safe-url.pipe';
import { CustomDatePipe } from '@pipes/custom-date/custom-date.pipe';
import { CurrencyIconComponent } from '@core-components/currency-icon/currency-icon.component';
import { MiniStoryComponent } from '@common-components/mini-story/mini-story.component';
import { SipTopBannerComponent } from '@common-components/sip-top-banner/sip-top-banner.component';
import { EndFundraiserBannerComponent } from '@common-components/end-fundraiser-banner/end-fundraiser-banner.component';
import { FundraiserSipbannerComponent } from '@common-components/fundraiser-sipbanner/fundraiser-sipbanner.component';
import { ThankyouBannerComponent } from '@common-components/thankyou-banner/thankyou-banner.component';
import { PlaceholderLoaderComponent } from '@common-components/placeholder-loader/placeholder-loader.component';
import { BenficiaryCardComponent } from '@common-components/benficiary-card/benficiary-card.component';
import { ExtraProductSelectComponent } from '@core-components/extra-product-select/extra-product-select.component';
import { HospitalDoctorContactComponent } from '@common-components/hospital-doctor-contact/hospital-doctor-contact.component';
import { StoryDocsComponent } from '@common-components/story-docs/story-docs.component';
import { DonationMethodsComponent } from '@common-components/donation-methods/donation-methods.component';
import { DonationMethodsV2Component } from '@common-components/donation-methods-v2/donation-methods-v2.component';
import { ReferFriendComponent } from '@common-components/refer-friend/refer-friend.component';
import { MultiPatientNoteComponent } from '@common-components/multi-patient-note/multi-patient-note.component';
import { SupportersComponent } from '@common-components/supporters/supporters.component';
import { ViewSupportersComponent } from '@common-components/view-supporters/view-supporters.component';
import { SupportCardComponent } from '@common-components/support-card/support-card.component';
import { PaymentComponent } from '@common-components/payment/payment.component';
import { ButtonComponent } from '@common-components/button/button.component';
import { EndedFundraiserV2Component } from '@common-components/ended-fundraiserV2/ended-fundraiser-v2.component';
import { SocialNumberComponent } from '@common-components/social-number/social-number.component';
import { SocialShareBtnComponent } from '@common-components/social-share-btn/social-share-btn.component';
import { DonateNowComponent } from '@common-components/donate-now/donate-now.component';
import { PaymentOtdComponent } from '@common-components/payment-otd/payment-otd.component';
import { PaymentOtdBtnComponent } from '@common-components/payment-otd-btn/payment-otd-btn.component';
import { SocialStripComponent } from '@common-components/social-strip/social-strip.component';
import { BannerComponent } from '@common-components/banner/banner.component';
import { FaqsComponent } from '@common-components/faqs/faqs.component';
import { StoryTabsComponent } from './components/story-tabs/story-tabs.component';
import { UtmPopupComponent } from './components/utm-popup/utm-popup.component';

@Component({
  selector: 'app-story',
  standalone: true,
  imports: [
    NgIf,
    NgFor,
    NgTemplateOutlet,
    AsyncPipe,
    FormsModule,
    MatIconModule,
    MatSlideToggleModule,
    MatRippleModule,
    MatExpansionModule,
    InViewportModule,
    ComponentLoader,
    CustomTranslatePipe,
    InrFormatPipe,
    SafeUrlPipe,
    CustomDatePipe,
    CurrencyIconComponent,
    MiniStoryComponent,
    SipTopBannerComponent,
    EndFundraiserBannerComponent,
    FundraiserSipbannerComponent,
    ThankyouBannerComponent,
    PlaceholderLoaderComponent,
    BenficiaryCardComponent,
    ExtraProductSelectComponent,
    HospitalDoctorContactComponent,
    StoryDocsComponent,
    DonationMethodsComponent,
    DonationMethodsV2Component,
    ReferFriendComponent,
    MultiPatientNoteComponent,
    SupportersComponent,
    ViewSupportersComponent,
    SupportCardComponent,
    PaymentComponent,
    ButtonComponent,
    EndedFundraiserV2Component,
    SocialNumberComponent,
    SocialShareBtnComponent,
    DonateNowComponent,
    PaymentOtdComponent,
    PaymentOtdBtnComponent,
    SocialStripComponent,
    BannerComponent,
    FaqsComponent,
    StoryTabsComponent
  ],
  templateUrl: './story.component.html',
  styleUrls: ['./story.component.scss']
})
export class StoryComponent {

  private readonly storage = inject(StorageService);
  public actRoute = inject(ActivatedRoute);
  private homepageService = inject(HomepageService);
  public util = inject(UtilService);
  private fundraiserService = inject(FundraiserService);
  readonly vars = inject(VariablesService);
  private seoService = inject(SeoService);
  private fbService = inject(FbService);
  private events = inject(EventsService);
  public dialog = inject(MatDialog);
  private paymentUtil = inject(PaymentUtilService);
  public vwo = inject(VwoService);
  private auth = inject(AuthService);
  public bottomSheet = inject(MatBottomSheet);
  private cdr = inject(ChangeDetectorRef);
  @ViewChild('youtubeEmbed') public youtubeEmbed: ElementRef<HTMLElement>;
  @ViewChild('tabs') public tabs: StoryTabsComponent;
  isMobile = this.vars.isMobile;
  fundraiser: IFundraiser;
  customTag;
  updates;
  comments;
  currency;
  kCartCurrency;
  paymentCurrency;
  isLoggedIn: boolean;
  donationCard;
  sharyCard;
  isDonated: boolean;
  userData: IUser;
  isFundraiserEnded: boolean;
  isSuccessfullyFunded: boolean;
  basicInfo = {} as IBasicInfo;
  beneficiary;
  theaterImage;
  sharedCounts = {
    fb: 0,
    whatsapp: 0
  };
  campaignUrl;
  videoSrc;
  currencySubscription: Subscription;
  actRouteSubs;
  tags;
  lastScrollTop = 0;
  today = new Date();
  inViewPort = {
    'docs': false,
    'comments': false,
    'twitter': false,
    'expense': false
  };
  varaibles = {
    showDonorImpact: true,
  };
  qParams;
  thankYouBanner: any;
  tickrIsShown: boolean;
  paymentDialogType = 'onpage';
  donationTitle = 'Stories.donations.chooseAmtToDonate';
  isShortStory = false;
  validLanguages = ['en', 'hi', 'ta', 'te', 'ar', 'ur'];
  initialTransalation = true;
  currentLanguage = 'en';
  translateObs;
  tabIndex = 0;

  subs: Subscription[] = [];
  isSipStory = false;
  globalVar;
  miniStory;
  readMoreStoryBtn = false;
  hide_el = true;
  eminentExpandEventSend = false;
  isSipSubscribed;
  eminentVisible = true;
  birthdayloader = true;
  prevOrder;
  loginPopupState = false;

  showEndCampaignPage: boolean = false;
  endedFundraiserTitle: string;
  endedFundraiserSubtitle: string | null;
  similarFundraisers;
  trendingFundraiserData;
  endedCampaignPageData = [];
  endedCampaignPageDataType

  constructor() {
    console.log('story load');
    if (this.vars.thirdPartyApp) {
      this.vars.layoutConfig.next({
        showSecure: true, showHeader: true, showFooter: false,
        animateHeader: false, showCurrency: false, logoNavigate: false, headerClass: '', zendesk: false,
        headerTopSticky: false,
        hideAbandonedCart: true
      });
    } else {
      this.vars.layoutConfig.next({
        showSecure: true, showHeader: true, showFooter: false,
        animateHeader: false, showCurrency: true, headerClass: '', zendesk: false, showLanguage: true,
        showDownloadApp: true,
        headerTopSticky: false,
        hideAbandonedCart: true
      });
    }
  }

  ngOnInit() {
    console.log('story load init');
    this.vars.selected_insurance = null;
    if (this.vars.isVariableLogin && !this.vars.varLoginData.userdata) {
      const user = this.auth.userLoginWithEmail({ ...this.actRoute.snapshot.queryParams });
    }
    this.isSipStory = this.actRoute.snapshot.queryParams['sip_story'] ? true : false;
    this.thankYouBanner = this.storage.getFromSession('showBanner');
    this.translateObs = this.vars.languageObs.subscribe((currentLang: any) => {
      this.currentLanguage = currentLang;
      if (!this.initialTransalation) {
        this.vars.showKCart = false;
        this.vars.selectedKCart = null;
        this.getFundraiser(this.customTag, this.currency || 'INR');
      }
    });
    this.util.scrollToTop();
    if (typeof document !== 'undefined') {
      this.vars.document.body.classList.add('storyPage');
      this.vars.document.body.classList.add('dmsans');
    }
    this.checkUserLogin();
    this.getParam();
    this.vars.campaignType = 'stories';
    if (!this.vars.thirdPartyApp) {
      this.vars.logoOnclik.subscribe((res) => this.headerLogoClickListner(res));
    }

    this.subs.push(this.vars.globalVar.subscribe({
      next: (resp) => {
        this.globalVar = resp;
        this.checkForSipStory();
        if (resp && resp.mini_story && !this.readMoreStoryBtn && !this.isFundraiserEnded) {
          this.miniStory = resp.mini_story;
        }
      }
    }));
    this.subs.push(this.paymentUtil.paymentCurrency.subscribe(currency => {
      this.paymentCurrency = currency;
      this.checkForSipStory();
      if (this.vars.showKCart && this.paymentCurrency && !this.isMobile) {
        this.vars.kCartCurrencyChange.next({ currency: this.paymentCurrency, preSelect: false });
      }
    }));

    if (this.actRoute.snapshot.queryParams.mini_story) {
      this.miniStory = true;
      this.vars.pageName = 'mini_story';
    }
    if (!this.vars.clickedAnywhere && typeof window !== 'undefined') {
      document.addEventListener('click', () => {
        this.vars.clickedAnywhere = true;
      });
    }

    if (this.util.getUserData()?.email?.match(this.vars.dummyEmailExtn)) {
      this.getUpdatedUserData();
    }

    setTimeout(() => {
      this.birthdayloader = false;
    }, 3000);

    setTimeout(() => {
      if (this.vwo.story_blur_image_ABC() === "INDIVIDUAL") {
        this.vars.blur_image = true;
      }
    }, 500);

    this.actRoute.queryParams.subscribe((qParams) => {
      if (qParams.hasOwnProperty('show') && qParams.show === "endedPage") {
        this.showEndCampaignPage = true;
      } else {
        this.showEndCampaignPage = false;
      }
    });
  }

  initLeadsPopup() {
    // invoke after 30 secs
    setTimeout(() => {
      if (!this.isMobile) {
        this.vars.isFundraiserLeftDialog = true;
        this.dialog.closeAll();
        this.storage.add('fundraiserLeftPopupOpened', true);
        this.dialog.open(ContactDialogComponent, {
          data: {
            title: this.fundraiser.campaigner.full_name,
            id: this.fundraiser.id
          },
          width: '100vw',
          maxWidth: '500px',
          maxHeight: '98vh',
          panelClass: ['round-dialog', 'contact-dialog']
        });
      } else {
        this.storage.add('fundraiserLeftPopupOpened', true);
        this.dialog.closeAll();
        this.vars.isFundraiserLeftDialog = true;
        this.bottomSheet.open(ContactDialogComponent, {
          data: {
            title: this.fundraiser.campaigner.full_name,
            id: this.fundraiser.id
          },
          panelClass: 'contact-dialog'
        });
      }
    }, 30000);
  }

  /**Get the params from URL */
  getParam() {
    const qParams = this.actRoute.snapshot.queryParams;
    const sessionLang = this.storage.getFromSession('language');
    if (qParams.hasOwnProperty('hide_el')) {
      this.hide_el = !qParams.hide_el;
    }
    if (qParams['type'] === 'lead') {
      this.paymentDialogType = 'lead';
      this.donationTitle = 'Stories.donations.provideWithDetails';
      this.vars.paymentDialogType.next('lead');
    }
    if (qParams['st'] === 'f' && !this.vars.thirdPartyApp) {
      this.paymentUtil.isSubmitted.next(true);
      if (this.storage.get('walletOffer100')) {
        this.paymentUtil.utmProceedClicked = true;
      }
    }
    if (qParams['selected_currency'] && this.isMobile && !qParams['st']) {
      this.paymentUtil.isSubmitted.next(true);
    }
    if (qParams['short_story']) {
      this.isShortStory = true;
    }
    if (qParams['lang'] && this.validLanguages.includes(qParams['lang'])) {
      this.vars.languageObs.next(qParams['lang']);
      this.vars.tranlatedLanguage = qParams['lang'];
      this.storage.addSessionData('language', qParams?.lang);
    } else if (sessionLang) {
      this.vars.tranlatedLanguage = sessionLang;
      this.vars.languageObs.next(sessionLang);
    }


    this.actRoute.params.subscribe((params) => {
      this.customTag = params['tag'];
      this.seoService.createAmpHtml('stories', this.customTag);
      this.vars.showKCart = false;
      this.vars.selectedKCart = null;
      this.vars.isBrowser ? this.getCurrency() : this.getFundraiser(this.customTag, 'INR');
    });
    this.actRouteSubs = this.actRoute.queryParams.subscribe((params) => {
      this.qParams = params;
      if (!params.payment || ((!this.isMobile && (params.payment && params.payment === 'form')))) {
        if (!this.vars.qrOpened && !this.vars.showKCart) {
          this.dialog.closeAll();
        }
      }
      if (this.vars.isBrowser) {
        setTimeout(() => {
          if (params.focus) {
            this.util.scrollIntoViewSmoothly(params.focus);
          }
        }, 1400);
      }
    });
  }

  openUtmPopup(): void {
    this.dialog.open(UtmPopupComponent, {
      maxWidth: '100%',
      width: '284px',
      panelClass: 'utm-popup',
      data: {
        campaignId: this.fundraiser.id,
        name: this.fundraiser.campaigner.fname
      }
    });
  }

  /**check user logged in status */
  checkUserLogin() {
    this.vars.isLoggedIn$.subscribe((res) => {
      this.isLoggedIn = res;
    });
    this.vars.userData.subscribe({
      next: (res) => {
        if (typeof window !== 'undefined' && res) {
          this.userData = res;
          this.checkForSipStory();
          if (this.userData.aggdonationall && this.userData.aggdonationall.funds) {
            (<any>window).repeatedDonor = true;
          }
          this.isSipSubscribed = [1, 2].includes(res?.listsubscriptions?.medical?.status_flag);
        }
      }
    });
  }

  afterLoggedInChecks(campaignId) {
    const qParams = this.actRoute.snapshot.queryParams;
    if (qParams['shby'] && qParams['utm_content']) {
      this.getSharyCardDetails(campaignId, qParams['utm_content']);
    }
    if (this.userData) {
      this.getDonorCardDetails(campaignId, this.userData.id);
    } else {
      if (qParams['user_id']) {
        this.getDonorCardDetails(campaignId, qParams['user_id']);
      }
    }
  }
  /**Set the theatre image of the story */
  setTheatreImage() {
    if (this.fundraiser.theater) {
      this.theaterImage = this.fundraiser.theater.cdn_path;
    } else {
      const gallery = this.fundraiser ? this.fundraiser.gallery : [];
      this.theaterImage = gallery && gallery.length ? gallery[0].cdn_path : this.vars.defaultBlurImg;
    }
  }
  /**check the length of the array */
  checkLength(arr) {
    return arr && arr.length ? true : false;
  }
  /**
   * Get the currency of the from
   * currency observable
  */
  getCurrency() {
    this.currencySubscription = this.vars.currency$.subscribe({
      next: (res) => {
        if (res) {
          this.vars.showKCart = false;
          this.vars.selectedKCart = null;
          this.getFundraiser(this.customTag, res);
          this.currency = res.toUpperCase();
        }
      }
    });
  }

  /**Set title of the page */
  setTitle() {
    if (this.vars.thirdPartyApp) {
      this.util.setPageTitle('Ketto Crowdfunding Platform');
    } else {
      if (this.fundraiser.story_title) {
        this.util.setPageTitle(this.fundraiser.story_title.info_1);
      }
    }
  }
  getFundriserStatus(data: IFundraiser) {

    this.isFundraiserEnded = this.util.checkFundraiserEnded(data.status_flag, data.end_date);
    if (this.isFundraiserEnded) {
      this.miniStory = false;
      if (data.raised) {
        this.isSuccessfullyFunded = this.util.checkIfSuccessfullyFunded(data.amount_requested, data.raised.raised);
      }
      // Set verbiage for ended fundraiser
      if (this.isLoggedIn && this.isDonated) {
        this.endedFundraiserTitle = `Hello ${this.userData?.full_name?.toLowerCase() || ''}, Thanks for your contribution.`;
        this.endedFundraiserSubtitle = 'This fundraiser is not accepting funds anymore';
      } else {
        this.endedFundraiserTitle = 'This fundraiser is not accepting funds anymore';
        this.endedFundraiserSubtitle = null;
      }

      if (this.actRoute.snapshot.queryParams?.show !== 'default') {
        this.vars.router.navigate([], {
          queryParams: { show: 'endedPage' },
          queryParamsHandling: 'merge',
        });
      }

      // this.getSimilarFundraisersData(this.fundraiser.id)
    }
  }

  getSimilarFundraisersData(customTag) {
    this.endedCampaignPageData = [];
    const qParam = `?with=leaderboard&currency=${this.currency ? this.currency : 'inr'}`;
    this.fundraiserService.getSimilarFundraisers(customTag, qParam).subscribe((res: any) => {
      this.similarFundraisers = (res.data || []).map(item => {
        if (item?.parent_cause_id === 149 && item?.amount_requested) {
          const percent = ((item.raised.raised ?? 0) / item.amount_requested) * 100;
          if (percent > 80) {
            return {
              ...item,
              raised: {
                ...item.raised,
                raised: Math.floor(item.amount_requested * 0.8)
              }
            };
          }
        }
        return item;
      });
      if (res?.data?.length < 1) {
        this.getTrendingFundraisersData()
      } else {
        this.endedCampaignPageData = this.similarFundraisers;
        this.endedCampaignPageDataType = "SIMILAR_FUNDRAISER"
      }
    });
  }

  getTrendingFundraisersData() {
    const qparams = '&with=basicInfo';
    this.homepageService.getTrendingFR(this.currency, qparams).subscribe((res: any) => {
      this.trendingFundraiserData = res.data.data;
      this.endedCampaignPageData = this.trendingFundraiserData;
      this.endedCampaignPageDataType = "TRENDING_FUNDRAISER"
    });
  }

  /**Set Basic Info */
  setBasicInfo(data: any[]) {
    if (data && data.length) {
      for (const item of data) {
        this.basicInfo[item.info_type] = item.info_1 || '';
      }
      this.vars.basicInfo = this.basicInfo;
      if (this.basicInfo.translation && this.initialTransalation) {
        this.initialTransalation = false;
        this.vars.translateLangArray.next(this.basicInfo.translation.split(','));
        // this.validLanguages = this.basicInfo.translation.split(',');
      }
      this.setCampaigner();
    }
  }
  /**Set the logic of the
   * Campaigner(Owner) and Beneficiary card
   */
  setCampaigner() {
    this.beneficiary = {};
    if (this.fundraiser?.beneficiary?.full_name) {
      this.beneficiary = this.fundraiser.beneficiary;
    } else if (this.basicInfo.beneficiary_name) {
      this.beneficiary['full_name'] = this.basicInfo.beneficiary_name;
      this.beneficiary['relation'] = this.basicInfo.beneficiary_relation;
    } else {
      this.beneficiary = undefined;
    }
    this.vars.beneficiary = this.beneficiary;
  }
  /**Add youtube emabed on demand*/
  addYoutubeEmbed(url) {
    const data = {
      title: this.vars.metaDataOfPage.title,
      desc: this.vars.metaDataOfPage.description,
      videoId: this.util.getYouTubeId(url),
      startDate: this.vars.fundraiser.start_date
    };
    this.seoService.schamObjectForVideos(data);
    const id = this.util.getYouTubeId(url);
    this.videoSrc = 'https://www.youtube.com/embed/' + id;
  }
  /**Get the click on the logo of the header */
  headerLogoClickListner(data) {
    if (data) {
      this.util.scrollInto('scroll-about-us');
    }
  }
  /**Scroll to video if you click from banner */
  scrollToVideo(event) {
    if (event) {
      this.youtubeEmbed.nativeElement.scrollIntoView({
        behavior: 'smooth', block: 'center'
      });
    }
  }
  /**Add meta tags */
  addMetaTags() {
    const metaData = this.seoService.stroyPageMetaTags(this.fundraiser);
    this.tags = this.seoService.createTagObject(metaData);
    this.vars.metaDataOfPage = metaData;
    this.seoService.addMetaTags(this.tags);
  }
  /**On window scroll */
  @HostListener('window:scroll', []) onWindowScroll() {
    const _el = document.getElementById('urgentSlide');
    if (_el) {
      const st = window.pageYOffset || document.documentElement.scrollTop;
      if (this.vars.showKCart) {
        _el.style.bottom = st > this.lastScrollTop ? '0' : '95px';
      } else {
        _el.style.bottom = st > this.lastScrollTop ? '0' : this.vars.fundraiser?.is_multi_patient_campaign ? '88px' : '65px';
      }
      this.lastScrollTop = st <= 0 ? 0 : st;
    }
  }
  /**Get the name of the beneficiary */
  getFirstName(name: string) {
    return name ? name.split(' ')[0] : '';
  }
  /**In view port */
  inViewPortListner(e, type) {
    if (this.fundraiser && e.visible === true && !this.inViewPort[type] && this.vars.isBrowser) {
      if (type === 'comments') {
        this.getFundraiserCommnets(this.customTag);
      }
      this.inViewPort[type] = true;
    }
  }
  /**All API calls */
  /**Get the details of the fundraiser */
  getFundraiser(customTag, currency) {
    this.isDonated = false;
    this.vars.showKCart = false;
    this.vars.selectedKCart = null;
    const qParams = this.actRoute.snapshot.queryParams;
    this.fundraiser = undefined;
    let qParam = `?with=viewmedicalbill;gallery;basicinfo;beneficiary.avtar;campaigner.avtar;manager.entity;userFollowed;`
      .concat(`banktransfer;cause;campaigner.social;theater;storyTitle;storyDescription;leaderboard;doctorDetails;`)
      .concat(`hospitalOnly;videoAppeal;beneficiaryname;mediaBeneficiary;mswName;mswDate;ctaLabel;costbreakdown;isMultiPatient&currency=${currency}`)
      .concat(`&page_type=${this.vars.pageName}&short_story=${this.isShortStory}&lang=${this.currentLanguage}&device=${this.vars.deviceType}`);
    if (qParams?.story_version || qParams?.image_blur_type) {
      qParam = qParam + `&story_version=${qParams?.story_version || qParams?.image_blur_type}`;
    }
    if (this.vars.utmsUrlString) {
      qParam = qParam + `&${this.vars.utmsUrlString}`;
    }
    if (this.vwo.blur_image_AB()) {
      qParam = qParam + `&blur_image_AB=${this.vwo.blur_image_AB()}`;
    }
    this.fundraiserService.getCampaign(customTag, qParam).subscribe((res: any) => {
      if (!res?.data) {
        if (!this.vars.isBrowser) (globalThis as any).SSR_STATUS = 410;
        return;
      }
      if (res?.data?.login_required || res?.data?.parent_cause_id === 149) {
        this.vars.mandateLoginCheck = true;
      } else {
        this.vars.mandateLoginCheck = false;
      }
      if (res?.data?.show_product && !this.isMobile) {
        res['data']['tip_model'] = true;
      }
      this.vars.fundraiser = res.data;
      this.fundraiser = res.data;

      if (this.fundraiser?.is_multi_patient_campaign) {
        this.vars.donateMultiPatient = true;
      } else {
        this.vars.donateMultiPatient = false;
      }

      if (this.fundraiser?.show_product) {
        this.vars.showKCart = true;
        this.vars.kCartCurrencyChange.next({ currency: currency, preSelect: true });
      } else {
        this.vars.showKCart = false;
      }

      this.util.setFrequentVisitCampaign(res?.data?.campaign_id);
      this.checkForSipStory();

      if (this.qParams?.['utm_source'] === 'wallet_offer_100' && !this.storage.get('walletOffer100')) {
        // this.storage.add('walletOffer100', true);
        this.openUtmPopup();
      }
      this.fundraiserService.getCampaign(`${this.fundraiser.custom_tag}/raised`, `?currency=${this.currency}&page_type=${this.vars.pageName}`).subscribe((res2: any) => {
        this.fundraiser.raised = res2?.data?.raised;
        this.fundraiser.raised.beforeTickrBackers = this.fundraiser.raised ? this.fundraiser.raised.backers : 0;
        this.getFundriserStatus(this.fundraiser);
      });
      if (!this.tickrIsShown) { // This is to maintain tickr does not start again upon currency change
        this.tickrIsShown = true;
      }
      this.addMetaTags();
      if (!(this.fundraiser.story_description && this.fundraiser.story_description.info_1)) {
        this.util.navigate('/new/404');
      }
      this.campaignUrl = this.vars.domainData.url + this.vars.router.url;
      this.setTitle();
      if (this.vars.isBrowser) {
        this.getSocialShares();
        this.afterLoggedInChecks(this.fundraiser.id);
        this.getFundraiserUpdates(this.fundraiser.custom_tag, 1);
      }
      this.setTheatreImage();
      this.setBasicInfo(this.fundraiser.basicinfo);
      if (this.fundraiser.video_appeal) {
        this.addYoutubeEmbed(this.fundraiser.video_appeal.path);
      }
      if (this.vars.isBrowser) {
        this.pageViewEvent();
      }
      this.campaignVisitTrack();
      this.checkMobileVerify();
      this.getSimilarFundraisersData(this.fundraiser?.id)
      if (this.userData?.id) {
        this.getDonorCardDetails(this.fundraiser.id, this.userData?.id);
      }
    }, (errorRes) => {
      if (!this.vars.isBrowser) {
        (globalThis as any).SSR_STATUS = 410;
        return;
      }
      this.vars.router.navigate(['/new/404'], { queryParams: { redirectURL: this.vars.domainData.url + this.vars.router.url } });
    });
  }

  checkMobileVerify() {
    if (this.userData?.id || !this.fundraiser?.settings?.verify_mobile) { return; }
    const leadData: any = this.storage.get('leadGen');
    if (leadData?.ext && leadData?.phone) {
      this.vars.leadGenData$.next(leadData);
      return;
    }
    const visitCamps: any[] = this.storage.getFromSession('visitedCamps') || [];
    setTimeout(() => {
      this.loginPopupState = true;
    }, visitCamps.includes(this.fundraiser?.id) ? 10 : 5000);
    if (!visitCamps.includes(this.fundraiser?.id)) {
      visitCamps.push(this.fundraiser?.id);
    }
    this.storage.checkFromSession('visitedCamps', visitCamps);
  }

  leadGenClosed = (event) => {
    this.userData = this.vars.userData.getValue();
    this.loginPopupState = false;
  }


  /**Get fundraiser updates */
  getFundraiserUpdates(customTag, limit) {
    const qParam = `?with=owner.avtar&limit=${limit}&orderBy=creation_time&sortedBy=desc`;
    this.fundraiserService.getFundraiserUpdates(customTag, qParam).subscribe((res: any) => {
      this.updates = res.data;
      this.updates.data['facebook'] = {
        label: 'Spread the word',
        count: this.sharedCounts.fb,
        campaign_url: this.vars.domainData.url + this.vars.router.url
      };
      for (let i = 0; i < this.updates.data.length; i++) {
        res.data.data[i].owner['creation_time'] = res.data.data[i].creation_time;
        if (!this.updates.data[i].user_liked) {
          this.updates.data[i].user_liked = { 'status': 0 };
        }
        if (!this.updates.data[i]['comments']) {
          const qParam1 = `/${this.updates.data[i].id}/comments?limit=5`;
          this.fundraiserService.getFundraiserUpdates(customTag, qParam1).subscribe((res1: any) => {
            this.updates.data[i]['comments'] = res1.data;
            this.vars.getTranslatedJsonObs('Stories.referAFriend.writeSomethingToCheer').subscribe(translateRes => {
              this.updates.data[i]['comments'].placeholder = translateRes + (this.fundraiser?.campaigner?.fname || '');
            });
          });
        }
      }
    });
  }

  /**Get the fundraiser comments */
  getFundraiserCommnets(customTag) {
    const qParam = '?limit=5';
    this.fundraiserService.getFundraiserComments(customTag, qParam).subscribe((res: any) => {
      this.comments = res.data;
    });
  }
  /**Get donor card details */
  getDonorCardDetails(campaignId, userId) {
    const qParam = `?with=donoravtar;comments;activation&donor_entity_details_id=${userId}&sortedBy=desc&orderBy=id`;
    this.fundraiserService.getDonorCardDetails(campaignId, qParam).subscribe((res: any) => {
      this.donationCard = res.data;
      if (res.data && res.data.length) {
        this.isDonated = true;
        this.vars.fundraiser.isDonated = true;
      }
    });
  }
  /**Get Shary card details */
  getSharyCardDetails(campaignId, userId) {
    const qParam = `?with=donoravtar;comments;activation;donor&donor_entity_details_id=${userId}&sortedBy=desc&orderBy=creation_date`;
    this.fundraiserService.getSharyCardDetails(campaignId, qParam).subscribe((res: any) => {
      this.sharyCard = res.data.orders;
    });
  }
  /**Get social shared os the campaign */
  getSocialShares() {
    this.getFacebookShares();
    // this.getWatsappShare();
  }
  /**Get the fb shared */
  getFacebookShares() {
    const selectedId = this.fbService.getFacebookID();
    const url = `/stories/${this.customTag}&fields=engagement&access_token=${encodeURI(selectedId)}`;
    this.fundraiserService.getFacebookShares(url).subscribe((res: any) => {
      const count = res.engagement.share_count;
      if (!this.isFundraiserEnded) {
        this.sharedCounts = {
          fb: count < 500 ? Math.round(Math.random() * 101 + 800) : count + count * 0.2,
          whatsapp: 0
        };
        // this.sharedCounts.whatsapp = Math.round(count - count * 0.1);
      } else {
        this.sharedCounts.fb = count;
      }
    });
  }
  /**Get Whats app share */
  getWatsappShare() {
    this.sharedCounts.whatsapp = this.fundraiserService.getWhatsappShares();
  }

  pageViewEvent() {
    const pageViewPayload = {
      eventName: 'pageView',
      event_type_id: this.fundraiser.id,
      event_type: 'campaign',
      info_1: this.currency,
      info_3: 'stories'
    };
    this.events.onLoadSystemEvent(pageViewPayload);
    const campaignType = `${this.util.getCampaignTypeFromId(this.fundraiser.parent_cause_id)}`;
    const gtmData = {
      'event': 'productDetail',
      'currency': this.currency,
      'ecommerce': {
        'detail': {
          'actionField': { 'list': `${this.vars.pageName}` }, // actions have an optional list property.
          'products': [{
            'name': `${this.fundraiser.custom_tag}`, // Campaign Name
            'id': `${this.fundraiser.id}`, // Campaign ID.
            'category': campaignType,  // Campaign Type
            'variant': `${this.fundraiser.cause_id}`,
            'dimension5': '', // cd_Fundraiser Attributes
            'dimension7': `${this.fundraiser.cause_id}`, // cd_Campaign Cause ID
            'dimension8': `${this.fundraiser.custom_tag}`, // cd_Campaign Custom Tag
            'dimension9': `${this.fundraiser.id}`, // cd_Campaign ID
            'dimension10': campaignType // cd_Campaign Type
          }]
        }
      }
    };
    this.events.gtmPush(gtmData);

    const claverTap = {
      'Campaign Id': this.fundraiser.id,
      'Page URL': this.vars.domainData.url + this.vars.router.url,
      'Campaign Title': this.fundraiser.title,
      'Browser': this.util.detectBrowser(),
      'Campaign Type': this.util.getCampaignTypeFromId(this.fundraiser.id),
      'Cause ID': this.fundraiser.cause_id,
      'Device Type': this.isMobile ? 'Mobile' : 'Desktop',
      'Campaign image URL': this.fundraiser?.leaderboard?.cdn_path || '',
      'Custom Tag': this.fundraiser?.custom_tag || '',
      'campaign expiry date': this.fundraiser.end_date,
      'info_2': this.fundraiser?.parent_cause_id?.toString() || ''
    };
    const alreadyDonated = this.storage.getFromSession(this.fundraiser.custom_tag);
    if (alreadyDonated) claverTap['donation_completed_same_session'] = true;

    this.events.claverTapPush('Campaign Viewed', claverTap);
  }

  /** Track user on load  */
  campaignVisitTrack() {
    const user = this.util.getUserData();
    const payload = {
      source: this.vars.pageName
    };
    if (user) {
      this.fundraiserService.campaignVisitTrack(this.fundraiser.id, user.id, payload).subscribe();
    }
  }

  checkForSipStory() {
    if (this.globalVar && this.globalVar.sipStoryAB
      && this.fundraiser && this.fundraiser.is_sip_story
      && !this.userData?.listsubscriptions?.medical && this.paymentCurrency === 'INR'
    ) {
      this.isSipStory = true;
    } else if (this.userData && this.userData?.listsubscriptions?.medical) {
      this.isSipStory = false;
    }
  }

  eminentExpand(e) {
    this.eminentVisible = !this.eminentVisible;
  }

  eminentEvent() {
    if (this.eminentExpandEventSend) {
      return;
    }
    const payload = {
      eventName: 'eminentExpanded',
    };
    this.events.sendSystemEvent(payload).subscribe(_ => _);
    this.eminentExpandEventSend = true;
  }

  ngOnDestroy() {
    if (this.currencySubscription) {
      this.currencySubscription.unsubscribe();
    }
    if (this.translateObs) {
      this.translateObs.unsubscribe();
    }
    if (this.actRouteSubs) {
      this.actRouteSubs.unsubscribe();
    }
    this.seoService.removeMetaTags(this.tags);
    if (typeof document !== 'undefined') {
      this.vars.document.body.classList.remove('storyPage');
      this.vars.document.body.classList.remove('dmsans');
    }
    this.seoService.removeAmpHtml();

    if (this.showEndCampaignPage) {
      this.events.sendSystemEvent({
        eventName: 'user_exited_without_action',
        event_type: 'campaign',
        event_type_id: this.fundraiser.id,
        page_name: 'EndedCampaignPage',
      })
    }

    this.subs.forEach(s => s.unsubscribe());
  }

  getTabIndex(event): void {
    this.tabIndex = event;
  }

  getDisease(basicInfo) {
    return basicInfo.find(item => item.info_type === 'disease').info_1;
  }

  toggleBlur() {
    this.vars.blur_image = !this.vars.blur_image;
    const ePayload = {
      eventName: 'story_blur_images',
      info_1: this.vars.blur_image ? "ON" : "OFF"
    };
    this.events.sendSystemEvent(ePayload).subscribe(_ => _);
  }

  handleGoToFundraiser() {
    this.vars.router.navigate([], {
      queryParams: { show: 'default' },
      queryParamsHandling: 'merge',
    });
  }

  handleGoToFundraiserUpdates() {
    this.vars.router.navigate([], {
      queryParams: { show: 'default' },
      queryParamsHandling: 'merge',
    });
    setTimeout(() => {
      this.tabs.onTabChange('Updates');
      this.cdr.detectChanges();
      window.scrollTo({
        top: document.body.scrollHeight * 0.5,
        behavior: 'smooth'
      });
    }, 1000);
  }

  // me api call
  getUpdatedUserData() {
    return new Promise((resolve, reject) => {
      this.auth.getUserProfile().subscribe({
        next: (res: any) => {
          this.util.setUserData(res.data);
          this.vars.userData.next(res.data);
          resolve(true);
        },
        error: (error) => {
          reject(false);
        }
      });
    });
  }

}
